import { useState } from 'react';
import Button from '../../../shared/components/Button';
import { Input } from '../../../shared/components/Input';
import { Select } from '../../../shared/components/Select';
import { useToast } from '../../../shared/components/toast/useToast';
import { useReferralCodes, useReferralPrograms } from '../hooks/useReferrals';
import { REFERRER_TYPE_LABELS, type ReferralCode, type ReferrerType } from '../types';
import { CodeFormModal } from './CodeFormModal';

const ALL = '';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });

/**
 * Referral codes, one or more per referrer. Type and active filters run server-side; the
 * referrer search is client-side over the loaded list (the controller has no name filter).
 */
export function CodesPanel({ canManage }: { canManage: boolean }) {
  const { toast } = useToast();
  const [referrerType, setReferrerType] = useState(ALL);
  const [active, setActive] = useState(ALL);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ReferralCode | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: codes = [], isLoading, error } = useReferralCodes({
    referrerType: (referrerType || undefined) as ReferrerType | undefined,
    isActive: active === ALL ? undefined : active === 'true',
  });
  const { data: programs = [] } = useReferralPrograms();
  const programName = new Map(programs.map((p) => [p.id, p.name]));

  const needle = search.trim().toLowerCase();
  const visible = needle
    ? codes.filter(
        (c) =>
          c.referrerDisplayName.toLowerCase().includes(needle) ||
          c.referrerEmail.toLowerCase().includes(needle) ||
          c.code.toLowerCase().includes(needle),
      )
    : codes;

  const copy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`Copied ${code}`);
    } catch {
      toast.error('Could not copy — select the code and copy it manually.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          label="Referrer"
          className="w-56 min-h-10 lg:min-h-0"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Name, email or code"
        />
        <Select
          label="Referrer type"
          size="small"
          className="w-48"
          options={[
            { value: ALL, title: 'All types' },
            ...Object.entries(REFERRER_TYPE_LABELS).map(([value, title]) => ({ value, title })),
          ]}
          value={referrerType}
          onChange={setReferrerType}
        />
        <Select
          label="Status"
          size="small"
          className="w-44"
          options={[
            { value: ALL, title: 'Active and inactive' },
            { value: 'true', title: 'Active only' },
            { value: 'false', title: 'Inactive only' },
          ]}
          value={active}
          onChange={setActive}
        />
        {canManage && (
          <Button size="sm" className="h-[40px] ml-auto" onClick={() => setCreating(true)}>
            New code
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-red-500">Could not load referral codes.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {!isLoading && !error && visible.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {codes.length === 0 ? 'No referral codes yet.' : 'No codes match these filters.'}
        </p>
      )}

      {visible.length > 0 && (
        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted">
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Code</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Referrer</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Reward rule</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground">Created</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {visible.map((code) => (
                  <tr key={code.id} className="border-b border-border">
                    <td className="px-4 py-3 font-mono font-medium text-foreground">{code.code}</td>
                    <td className="px-4 py-3 text-foreground">
                      {code.referrerDisplayName}
                      <span className="block text-xs text-muted-foreground">
                        {code.referrerEmail}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {REFERRER_TYPE_LABELS[code.referrerType]}
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {code.rewardProgramId
                        ? (programName.get(code.rewardProgramId) ?? 'Unknown rule')
                        : 'Platform default'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          code.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {code.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground">{formatDate(code.createdAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-[40px]"
                          onClick={() => copy(code.code)}
                        >
                          Copy
                        </Button>
                        {canManage && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-[40px]"
                            onClick={() => setEditing(code)}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {creating && <CodeFormModal onClose={() => setCreating(false)} />}
      {editing && <CodeFormModal code={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}
